import "./required-update-password.scss"
import PasswordValidator from "password-validator";

import { autoinject, observable, bindingMode, bindable } from "aurelia-framework";
import { Router } from "aurelia-router";
import { validateTrigger, ValidationController, ValidationControllerFactory, ValidationRules } from "aurelia-validation";

import { ChallengeService, IPasswordStatusResult } from "../../services/challenge-service";
import { LoginStage } from "../../enums/login-stage";

@autoinject
export class RequiredUpdatePassword {

    @bindable({ defaultBindingMode: bindingMode.fromView })
    public stage: LoginStage;
    @bindable({ defaultBindingMode: bindingMode.fromView })
    public passwordResponse: IPasswordStatusResult;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public finalize: () => Promise<void>;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public showSuccessMessage: boolean;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public returnUrl: string;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public passwordCheckResponse: any;
    private _service: ChallengeService;
    private _router: Router;
    public controller: ValidationController;
    public isLoading = false;
    public hasError = false;

    @observable
    public currentPassword: string;
    @observable
    public newPassword: string;
    @observable
    public confirmPassword: string;
    public showCurrentPassword: boolean = false;
    public showNewPassword: boolean = false;
    public showConfirmPassword: boolean = false;
    public showCurrentPasswordEye: boolean = false;
    public showNewPasswordEye: boolean = false;
    public showConfirmPasswordEye: boolean = false;
    public showUpperCaseError: boolean = false;
    public showMinCharError: boolean = false;
    public showLowerCaseError: boolean = false;
    public showNumberError: boolean = false;  
    public isPasswordValidated: boolean = false;
    public errorMessage: string;
    public newPasswordError: string;
    public currentPasswordError: string;

    public get canSubmit(): boolean {
        return this.isPasswordValidated &&
            !this.showUpperCaseError &&
            !this.showLowerCaseError &&
            !this.showNumberError &&
            !this.showMinCharError;
    }

    public constructor(controllerFactory: ValidationControllerFactory, service: ChallengeService, router: Router) {
        this.controller = controllerFactory.createForCurrentScope();
        this.controller.validateTrigger = validateTrigger.changeOrBlur;
        this._service = service;
        this._router = router;
    }

    public async updatePassword() {
        const validationResult = await this.controller.validate();
        if (!validationResult.valid || !this.isPasswordValidated) {
            return;
        }

        this.isLoading = true;
        let result;
        try {
            result = await this._service.updatePasswordChallenge(this.currentPassword, this.newPassword);
        } catch(error) {
            if(error.status == 400) {
                this.newPasswordError = error.response.newPassword;
                this.currentPasswordError =  error.response.error;
            } else {
                this.hasError = true;  
            }                      
        } finally {
            this.isLoading = false;
        }
        
        if (result != null) {
            if (this.passwordCheckResponse) {
                this.passwordCheckResponse.isPasswordChangeRequired = false;
            }
            this.showSuccessMessage = true;
            await this.finalize();           
        }
    }

    public toggleShowCurrentPassword() {
        this.showCurrentPassword = !this.showCurrentPassword;
    }
    public toggleShowNewPassword() {
        this.showNewPassword = !this.showNewPassword;
    }
    public toggleShowConfirmPassword() {
        this.showConfirmPassword = !this.showConfirmPassword;
    }
    public currentPasswordChanged(newValue: any) {
        if(newValue) {
            this.showCurrentPasswordEye = true;
            this.currentPasswordError = null;
        } else {
            this.showCurrentPasswordEye = false;
        }    
    }
    public newPasswordChanged(newValue: any) {
        const schema = new PasswordValidator();
        schema.is().min(12)
        .is().max(256)
        .has().uppercase()
        .has().lowercase()
        .has().digits()
        .has().not().spaces();

        function hasUpperCase(str:string) {
            return str !== str.toLowerCase();
        }
        function hasLowerCase(str:string) {
            return str !== str.toUpperCase();
        }
        function hasNumber(str:string) {
            return /\d/.test(str);
        }

        if(newValue) {
            this.showNewPasswordEye = true;
            this.newPasswordError = null;
            if(hasUpperCase(this.newPassword)) {
                this.showUpperCaseError = false;
            } else {
                this.showUpperCaseError = true;
            }
    
            if(hasLowerCase(this.newPassword)) {
                this.showLowerCaseError = false;
            } else {
                this.showLowerCaseError = true;
            }
            if(hasNumber(this.newPassword)) {
                this.showNumberError = false;
            } else {
                this.showNumberError = true;
            }
            if(this.newPassword.length >= 12) {
                this.showMinCharError = false;
            } else {
                this.showMinCharError = true;
            }
        } else {
            this.showNewPasswordEye = false;
        }

        this.isPasswordValidated = schema.validate(this.newPassword) as boolean;
        if (this.confirmPassword) {
            this.controller.validate({ object: this, propertyName: "confirmPassword" });
        }
    }
    public confirmPasswordChanged(newValue: any) {
        if(newValue) {
            this.showConfirmPasswordEye = true;
        } else {
            this.showConfirmPasswordEye = false;
        }
        this.controller.validate({ object: this, propertyName: "confirmPassword" });
    }
    public cancelUpdatePassword() {
        if(!this.passwordCheckResponse || this.passwordCheckResponse.isPasswordChangeRequired) {
            window.location.reload();
        } else {
            window.location.replace(this.returnUrl);
        }
    }
}

ValidationRules
    .ensure((x: RequiredUpdatePassword) => x.currentPassword).required().minLength(8).maxLength(256)
    .ensure((x: RequiredUpdatePassword) => x.newPassword).required()
    .ensure((x: RequiredUpdatePassword) => x.confirmPassword).satisfies((v, p) => v == p.newPassword).withMessage("Passwords must match.")
    .on(RequiredUpdatePassword);